import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import {
  ENDPOINTS,
  type ContractTypeAmendmentSignalRow,
  type ContractTypeConfirmedNewerRow,
  type ContractTypeProposalsResponse,
  type ContractTypeReading,
  type ContractTypeUndecidedRow,
  type DecideContractTypeResponse,
} from '@clm/shared-types';

import { dismissAmendmentSignal, fetchContractTypeProposals } from '../api/contract-type-proposals';
import { ApiError } from '../api/errors';
import { classifyApiError, type ApiProblemKind } from '../api/problem';
import { ContractTypeTag } from '../components/ContractTypeTag';
import { DataTable, type DataTableColumn } from '../components/DataTable';
import { ErrorNotice } from '../components/ErrorNotice';
import { requireSurface } from '../routes/surfaces';
import { refusalCodeOf } from './base-msa-shared';
import { ContractTypeDecisionPanel, ContractTypeDecisionReceipt } from './ContractTypeDecisionPanel';
import { decisionActions, subjectOfRow, type DecisionAction } from './contract-type-decision';
import { PanelProblem } from './quality/panel';
import { usePanel } from './quality/panel-state';
import {
  AUTO_CONFIRMATION_KIND_COPY,
  DECISION_KIND_COPY,
  SAFETY_SAMPLE_TAG,
  SAFETY_SAMPLE_TAG_TITLE,
  TYPE_PROPOSALS_FORBIDDEN_COPY,
  TYPING_GUIDE,
  amendmentSignalWords,
  contractTypeSourceWords,
  nameEvidenceWords,
  typeProposalsEmptyCopy,
  type TypeProposalsTab,
} from './type-proposal-copy';
import {
  TYPE_PROPOSALS_TAB_LABEL,
  TYPE_PROPOSALS_TABS,
  typeProposalCapLine,
  typeProposalRowsOf,
  typeProposalTabCounts,
} from './type-proposals-tabs';

/**
 * Admin **Type proposals** — the type-decision list · CLM-110 (FR-35, FR-34,
 * FR-36, DR-10, CR-08; D-110-1, D-110-2; R-110-17 … R-110-22) and CLM-118
 * (D-110-3; R-118-16).
 *
 * The type comes from the file name first and the AI's reading second
 * (D-110-3). When the file name gives MSA, NDA or SOW and the AI's reading
 * agrees, the type is confirmed with no administrator step (`auto_confirmed`).
 * When they disagree, or the name gives no type it can trust, the type waits
 * here for an Admin. One read, `GET /v1/admin/contract-type-proposals` (v3),
 * gated `admin:configure` and scoped on the API side; one write,
 * `decide-contract-type`, per decision; and `dismiss-amendment-signal` for a
 * "possibly an amendment" flag.
 *
 * ## Seven lists, by cause (R-118-16)
 *
 * Every listed agreement is on exactly one tab. Type pending is a state, not a
 * reason, so its tab holds every pending agreement whatever its reason; the
 * other undecided tabs hold provisional agreements only:
 *
 *  - **AI disagrees** (the default) — the AI read another type, or gave no
 *    usable type (`ai_no_type`, R-110-28);
 *  - **AI decided — verify** — the name gave no type it could trust, so the
 *    AI's type was applied as the working type (case 2): an Admin verifies it;
 *  - **Type pending** — no type yet: the name gave none (or two), and the AI's
 *    reading was a SOW with no number, was held by a guard, or has not landed;
 *  - **AI agrees — verify** — the AI read the type the contract already holds
 *    from a person or an unrecorded rule;
 *  - **Not read by AI** — processing-restricted (never read, rule 4), waiting
 *    for a re-run, or with no reading. The no-reading list is capped at the
 *    newest 200 (Type pending first), and counted in full;
 *  - **Confirmed — a newer reading disagrees** — a family settled by an Admin,
 *    or by the file name and the AI, whose reading newer than the settlement
 *    disagrees;
 *  - **Possibly an amendment** — an agreement whose name or reading suggests it
 *    amends another, with **Dismiss**. Linking it is CLM-120.
 *
 * The header counts the agreements still waiting for the AI (they have nothing
 * to decide yet, and the decision endpoint refuses them) and those confirmed
 * because the file name and the AI agree.
 *
 * ## Deciding (D-110-2: Admin only)
 *
 * Each row offers **Accept {AI type}**, **Keep {current}** and **Correct…**, or
 * **Confirm {current}** and **Correct…** when there is no usable reading; a
 * Type pending row offers **Accept {AI type}** and **Type…**, never "Confirm
 * OTHER". Each opens the shared decision panel, which previews the family rows
 * it writes, the SOW number it needs or clears, and the base refusal, and posts
 * the row's `seen` block back unchanged. A decision sets the agreement and
 * every amendment in one audited UPDATE, and **never changes a contract ID**
 * (FR-35). The page is Admin-only, so there is no role hint here: the API's 403
 * is the answer for anyone else, in the page's own words.
 *
 * ## Agreement, not accuracy (R-110-7, kept)
 *
 * The totals compare the AI's reading with the type **now**, which is itself a
 * guess until confirmed — so the page says *agreement with the current type*.
 * FR-34's accuracy is counted from the Admins' decisions, on the Extraction
 * accuracy page (R-110-18). Readings on amendments and on Type pending
 * placeholders are never compared on their own.
 */

const ENDPOINT_PATH = ENDPOINTS['contract-type-proposals'].path;
const PANEL = 'Type proposals';

type Row = ContractTypeUndecidedRow | ContractTypeConfirmedNewerRow;


function readingCell(reading: ContractTypeReading | null, restricted: boolean): ReactNode {
  if (reading === null) {
    return <span className="mut">{restricted ? 'Never read — processing-restricted (§4C)' : 'Not read by the AI'}</span>;
  }
  return (
    <>
      <strong>{reading.proposed_type ?? 'no usable type'}</strong>
      <br />
      <span className="mut">
        <code>{reading.extraction_model}</code> · schema <code>{reading.extraction_schema_version}</code> · audit{' '}
        {reading.audit_id}
      </span>
    </>
  );
}

function flagsOf(row: Row): ReactNode {
  const flags: ReactNode[] = [];
  if (row.is_current_base) flags.push(<span key="base" className="tag acc">Current base</span>);
  if (row.lifecycle_status === 'Superseded') flags.push(<span key="sup" className="tag out">Superseded</span>);
  if (row.processing_restricted) flags.push(<span key="rst" className="tag bad">Restricted</span>);
  if ('reason' in row && row.reason === 'awaiting_rerun') {
    flags.push(<span key="rerun" className="tag up">Waiting for a re-run</span>);
  }
  /* CLM-121 (R-121-15) — visible, never blind: correcting a sample pauses auto-approval. */
  if ('auto_approval_sample' in row && row.auto_approval_sample) {
    flags.push(
      <span key="sample" className="tag up" title={SAFETY_SAMPLE_TAG_TITLE} data-testid="type-proposal-safety-sample">
        {SAFETY_SAMPLE_TAG}
      </span>,
    );
  }
  return flags.length === 0 ? null : <span className="fl">{flags}</span>;
}

/** The settlement a newer reading disagrees with: an Admin's decision, or the act that names nobody (B8). */
function settledCell(row: ContractTypeConfirmedNewerRow): ReactNode {
  if (row.decision !== null) {
    return (
      <>
        {row.decision.decided_by_upn} · <time dateTime={row.decision.decided_utc}>{row.decision.decided_utc}</time>
        <br />
        <span className="mut">{DECISION_KIND_COPY[row.decision.kind]}</span>
      </>
    );
  }
  if (row.auto_confirmation !== null) {
    return (
      <>
        No administrator — {AUTO_CONFIRMATION_KIND_COPY[row.auto_confirmation.kind]} ·{' '}
        <time dateTime={row.auto_confirmation.settled_utc}>{row.auto_confirmation.settled_utc}</time>
        <br />
        <span className="mut">audit {row.auto_confirmation.audit_id}; no person has checked it</span>
      </>
    );
  }
  return null;
}

function columns(onAct: (row: Row, action: DecisionAction) => void, newer: boolean): readonly DataTableColumn<Row>[] {
  return [
    {
      id: 'contract',
      header: 'Contract',
      cell: (row) => (
        <>
          <Link to={`/contracts/record/${encodeURIComponent(row.contract_key)}`}>{row.contract_key}</Link>
          {row.counterparty_name === null ? null : <span className="mut"> · {row.counterparty_name}</span>}
          {flagsOf(row)}
        </>
      ),
    },
    {
      id: 'type',
      header: 'Type now',
      cell: (row) => (
        <>
          <ContractTypeTag
            type={row.contract_type}
            confirmation={row.seen.confirmation}
            autoKind={'auto_confirmation' in row ? (row.auto_confirmation?.kind ?? null) : null}
          />
          <br />
          <span className="mut">{contractTypeSourceWords(row.contract_type_source, row.seen.confirmation)}</span>
          {'name_evidence' in row ? (
            <>
              <br />
              <span className="mut">File name: {nameEvidenceWords(row.name_evidence)}</span>
            </>
          ) : null}
        </>
      ),
    },
    { id: 'reading', header: 'AI reading', cell: (row) => readingCell(row.proposal, row.processing_restricted) },
    {
      id: 'amendments',
      header: 'Amendments',
      cell: (row) =>
        row.amendments.length === 0 ? (
          <span className="mut">none</span>
        ) : (
          <ul className="tags-ro" aria-label={`Amendments of ${row.contract_key}`}>
            {row.amendments.map((amendment) => (
              <li key={amendment.contract_key}>
                <code>{amendment.contract_key}</code>{' '}
                <span className="mut">
                  {amendment.proposal === null
                    ? '· not read'
                    : `· AI read ${amendment.proposal.proposed_type ?? 'no usable type'}`}
                </span>
              </li>
            ))}
          </ul>
        ),
    },
    { id: 'sow', header: 'SOW number', cell: (row) => row.sow_number ?? <span className="mut">—</span> },
    ...(newer
      ? [
          {
            id: 'decided',
            header: 'Settled',
            cell: (row: Row) => ('decision' in row ? settledCell(row) : null),
          },
        ]
      : []),
    {
      id: 'actions',
      header: 'Decide',
      cell: (row) => (
        <span className="fl">
          {decisionActions(subjectOfRow(row)).map((action) => (
            <button
              key={action.id}
              type="button"
              className={action.id === 'accept' || action.id === 'confirm' ? 'btn sm pri' : 'btn sm qt'}
              aria-label={`${action.label} — ${row.contract_key}`}
              onClick={() => onAct(row, action)}
            >
              {action.label}
            </button>
          ))}
        </span>
      ),
    },
  ];
}

function signalColumns(
  dismissing: string | null,
  onDismiss: (row: ContractTypeAmendmentSignalRow) => void,
): readonly DataTableColumn<ContractTypeAmendmentSignalRow>[] {
  return [
    {
      id: 'contract',
      header: 'Contract',
      cell: (row) => (
        <>
          <Link to={`/contracts/record/${encodeURIComponent(row.contract_key)}`}>{row.contract_key}</Link>
          {row.counterparty_name === null ? null : <span className="mut"> · {row.counterparty_name}</span>}
        </>
      ),
    },
    {
      id: 'type',
      header: 'Type now',
      cell: (row) => <ContractTypeTag type={row.contract_type} confirmation={row.contract_type_confirmation} />,
    },
    { id: 'lifecycle', header: 'Lifecycle', cell: (row) => row.lifecycle_status },
    { id: 'why', header: 'Why it is flagged', cell: (row) => amendmentSignalWords(row) },
    { id: 'filed', header: 'Filed', cell: (row) => <time dateTime={row.filed_utc}>{row.filed_utc}</time> },
    {
      id: 'dismiss',
      header: 'Flag',
      cell: (row) => (
        <button
          type="button"
          className="btn sm qt"
          disabled={dismissing !== null}
          aria-label={`Dismiss — ${row.contract_key}`}
          onClick={() => onDismiss(row)}
        >
          {dismissing === row.contract_key ? 'Dismissing…' : 'Dismiss'}
        </button>
      ),
    },
  ];
}

export function TypeProposals() {
  const surface = requireSurface('type-proposals');
  const { state, reload } = usePanel<ContractTypeProposalsResponse>((abort) => fetchContractTypeProposals(abort), []);
  const [tab, setTab] = useState<TypeProposalsTab>('disagrees');
  const [open, setOpen] = useState<{ readonly row: Row; readonly action: DecisionAction } | null>(null);
  const [receipt, setReceipt] = useState<DecideContractTypeResponse | null>(null);
  const [dismissing, setDismissing] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<string | null>(null);
  const [dismissProblem, setDismissProblem] = useState<{
    readonly key: string;
    readonly kind: ApiProblemKind;
    readonly code: string | null;
    readonly correlation_id: string | null;
  } | null>(null);
  /*
   * The last list that loaded stays on screen while the reload after a decision
   * is in flight (the Base MSA page's rule), so the page does not blank out
   * under the receipt. What is shown is still only ever an API answer.
   */
  const [lastReady, setLastReady] = useState<ContractTypeProposalsResponse | null>(null);
  useEffect(() => {
    if (state.kind === 'ready') setLastReady(state.response);
  }, [state]);
  const shown = state.kind === 'ready' ? state.response : state.kind === 'loading' ? lastReady : null;

  function decided(response: DecideContractTypeResponse) {
    setReceipt(response);
    setOpen(null);
    reload();
  }

  async function dismiss(row: ContractTypeAmendmentSignalRow) {
    setDismissing(row.contract_key);
    setDismissProblem(null);
    setDismissed(null);
    try {
      await dismissAmendmentSignal(row.contract_key);
      setDismissed(row.contract_key);
      reload();
    } catch (error) {
      setDismissProblem({
        key: row.contract_key,
        kind: classifyApiError(error),
        code: refusalCodeOf(error),
        correlation_id: error instanceof ApiError ? error.correlation_id : null,
      });
      /* `no_amendment_signal`: the flag is gone already — show the list as it is now. */
      if (classifyApiError(error) === 'conflict') reload();
    } finally {
      setDismissing(null);
    }
  }

  const counts = shown === null ? null : typeProposalTabCounts(shown);
  const cap = shown === null ? null : typeProposalCapLine(shown, tab);

  return (
    <>
      <div className="hd">
        <div>
          <h2 id="type-proposals-heading">{surface.label}</h2>
          <p>{surface.summary}</p>
          <div className="refs">
            {surface.requirements.map((requirement) => (
              <span key={requirement}>{requirement}</span>
            ))}
          </div>
        </div>
      </div>

      <section className="card qpanel" aria-labelledby="type-proposals-heading" aria-busy={state.kind === 'loading'}>
        {state.kind === 'loading' && shown === null ? (
          <p role="status" aria-busy="true">
            Reading {PANEL}…
          </p>
        ) : null}

        {state.kind === 'problem' ? (
          <PanelProblem
            panel={PANEL}
            state={state}
            endpointPath={ENDPOINT_PATH}
            onRetry={reload}
            forbiddenCopy={TYPE_PROPOSALS_FORBIDDEN_COPY}
          />
        ) : null}

        {receipt === null ? null : <ContractTypeDecisionReceipt response={receipt} />}

        {shown === null || counts === null ? null : (
          <>
            <p className="mut">
              The type comes from the file name first and the AI’s reading second. When the file name gives MSA, NDA
              or SOW and the AI agrees, the type is confirmed with no administrator step. Otherwise it waits here for an
              administrator. When auto-approval for AI-decided types is on, most AI-decided MSAs and NDAs are approved by
              policy, and one in five still comes here as a safety sample. One decision sets the agreement and all its
              amendments, and never changes a contract ID.
            </p>

            {shown.scope_kind === 'practices' ? (
              <p className="banner up" role="status">
                These figures cover only the contracts in your scope, not the whole corpus.
              </p>
            ) : null}

            <p data-testid="type-proposal-totals">
              {shown.totals.proposals} AI readings · {shown.totals.agreements} agree with the current type ·{' '}
              {shown.totals.disagreements} disagree · {shown.totals.not_compared} not compared (amendments and Type
              pending)
            </p>
            <p className="mut">
              Agreement with the current type, not accuracy: the current type is itself a guess until it is confirmed.
              Accuracy is counted from administrators’ decisions on the Extraction accuracy page.
            </p>
            <p className="mut" data-testid="type-proposal-awaiting">
              Waiting for the AI: {shown.totals.awaiting_ai} · Confirmed by an administrator: {shown.totals.confirmed} ·
              Confirmed because the file name and the AI agree: {shown.totals.auto_confirmed - shown.totals.policy_approved}{' '}
              · Approved by policy: {shown.totals.policy_approved} · Safety samples waiting:{' '}
              {shown.totals.auto_approval_samples}
            </p>

            <details className="mut" data-testid="type-typing-guide">
              <summary>Typing guide — documents the vocabulary does not settle on its own</summary>
              <ul>
                {TYPING_GUIDE.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </details>

            <div className="mr-modes" role="tablist" aria-label="Type decisions">
              {TYPE_PROPOSALS_TABS.map((candidate) => (
                <button
                  key={candidate}
                  type="button"
                  role="tab"
                  id={`type-proposals-tab-${candidate}`}
                  aria-selected={tab === candidate}
                  aria-controls="type-proposals-panel"
                  onClick={() => {
                    setTab(candidate);
                    setOpen(null);
                  }}
                >
                  {TYPE_PROPOSALS_TAB_LABEL[candidate]} ({counts[candidate]})
                </button>
              ))}
            </div>

            <div role="tabpanel" id="type-proposals-panel" aria-labelledby={`type-proposals-tab-${tab}`}>
              {cap === null ? null : (
                <p className="mut" data-testid="type-proposal-cap">
                  {cap}
                </p>
              )}
              {tab === 'amendments' ? (
                <>
                  <p className="mut">
                    A name that says Amendment, Addendum, Change Order or Request, Variation or Extension, a
                    change-request SOW number, or a reading that names the agreement it amends or replaces. The flag
                    never changes the type. Dismiss it when the contract stands on its own.
                  </p>
                  {dismissed === null ? null : (
                    <p role="status" data-testid="type-signal-dismissed">
                      Flag on <code>{dismissed}</code> dismissed.
                    </p>
                  )}
                  {dismissProblem === null ? null : dismissProblem.kind === 'conflict' &&
                    dismissProblem.code === 'no_amendment_signal' ? (
                    <p className="banner up" role="status" data-testid="type-signal-gone">
                      <code>{dismissProblem.key}</code> is no longer flagged — it was dismissed or linked since the
                      list loaded. Nothing was written.
                    </p>
                  ) : (
                    <ErrorNotice kind={dismissProblem.kind} correlation_id={dismissProblem.correlation_id} />
                  )}
                  <DataTable
                    caption={TYPE_PROPOSALS_TAB_LABEL[tab]}
                    columns={signalColumns(dismissing, (row) => void dismiss(row))}
                    rows={shown.amendment_signals}
                    rowKey={(row) => row.contract_key}
                    busy={state.kind === 'loading'}
                    empty={<p>{typeProposalsEmptyCopy(tab, shown.totals)}</p>}
                  />
                </>
              ) : (
                <>
                  <DataTable
                    caption={TYPE_PROPOSALS_TAB_LABEL[tab]}
                    columns={columns((row, action) => setOpen({ row, action }), tab === 'newer')}
                    rows={typeProposalRowsOf(shown, tab)}
                    rowKey={(row) => row.contract_key}
                    busy={state.kind === 'loading'}
                    isCurrent={(row) => open?.row.contract_key === row.contract_key}
                    empty={<p>{typeProposalsEmptyCopy(tab, shown.totals)}</p>}
                  />
                  {open === null ? null : (
                    <ContractTypeDecisionPanel
                      key={`${open.row.contract_key}:${open.action.id}`}
                      subject={subjectOfRow(open.row)}
                      action={open.action}
                      onDecided={decided}
                      onCancel={() => setOpen(null)}
                      onReload={() => {
                        setOpen(null);
                        reload();
                      }}
                    />
                  )}
                </>
              )}
            </div>

            <p className="mut">
              Generated <time dateTime={shown.generated_utc}>{shown.generated_utc}</time>.
            </p>
          </>
        )}
      </section>
    </>
  );
}
